// libs
import React, { memo, useEffect, useState } from 'react';

// global components
import { CAROUSEL_TYPE } from 'shared/components/Carousel';

// components
import VideoPlayButton from './components/VideoPlayButton';

// hooks
import { useSelectedSKUMedias, useSelectedSKUTitle } from 'shared/store/details/hooks/selectors';

// helpers
import { getOptimisedImageUrl } from 'shared/helpers/utils';
import { getCarouselDimension } from './helper';

//analytics
import NFA from 'shared/helpers/analytics/NFAnalytics';

// constants
import { ROLES } from 'shared/constants';

// styles
import {
  Placeholder,
  PlaceholderImage,
  ImageContainer,
  Image,
  imgClassName,
  Carousel,
} from './styles';

// constants
import {
  IMAGE_WIDTH,
  IMAGE_HEIGHT,
  IMAGE_OPTION,
  SLIDES_TO_SHOW,
  SLIDE_MARGIN,
  NAV_STYLES,
  VIDEO_ASPECT_RATIO,
} from './constants';
import { TRACK_TYPE } from 'shared/helpers/analytics/constants';
import { PDP_EVENTS } from 'shared/helpers/analytics/constants/pdpEvents';
import useTheme from '@nykaa/ui-components/themes/hooks/useTheme';

// defs
interface IImageSelectorProps {
  selectedImage: number;
  setSelectedImage: (index: number) => void;
}

function ImageSelector({ selectedImage, setSelectedImage }: IImageSelectorProps) {
  const [showCarousel, setShowCarousel] = useState(false);
  const [slideItems, setSlideItems] = useState(1);
  const [carouselHeight, setCarouselHeight] = useState(0);

  const images = useSelectedSKUMedias();
  const productTitle = useSelectedSKUTitle();
  const theme = useTheme();

  NAV_STYLES.backgroundColor = theme.colors.surface;

  const trackFirstImageSwiped = () => {
    NFA.track({
      event: PDP_EVENTS.CAROUSEL_FIRST_IMAGE_SWIPED,
      type: TRACK_TYPE.EVENT,
    });
  };

  useEffect(() => {
    if (showCarousel === false) {
      const { visibleItems, carouselHeight: newCarouselHeight } = getCarouselDimension();
      setSlideItems(visibleItems);
      setCarouselHeight(newCarouselHeight);
      setShowCarousel(true);
    }
  }, [showCarousel, setShowCarousel]);

  if (showCarousel === false) {
    return (
      <Placeholder data-test="product-desktop-image">
        {images.slice(0, SLIDES_TO_SHOW).map((image, idx) => {
          const isVideoImage = image.mediaType === 'video';
          const imageUrl = isVideoImage ? image.thumbnailUrl : image.url;
          return (
            <PlaceholderImage
              key={`placeholder-image-${image.id || idx}`}
              backgroundImage={getOptimisedImageUrl(imageUrl, IMAGE_OPTION)}
              isSelected={idx === 0}
            >
              {isVideoImage && <VideoPlayButton />}
            </PlaceholderImage>
          );
        })}
      </Placeholder>
    );
  }

  const visibleImageIdx = slideItems;

  return (
    <Carousel
      type={CAROUSEL_TYPE.VERTICAL}
      slideWidth={IMAGE_WIDTH}
      slideHeight={IMAGE_HEIGHT}
      slidesToShow={slideItems}
      carouselHeight={carouselHeight}
      ignoreRightMargin
      customRightMargin={SLIDE_MARGIN}
      isBottomNav
      navCustomStyles={NAV_STYLES}
      sendFirstImageSwipeTracking={trackFirstImageSwiped}
    >
      {images.map((image, idx) => {
        const isVideoImage = image.mediaType === 'video';
        const imageSrc = isVideoImage ? image.thumbnailUrl : image.url;
        const aspectRatio = isVideoImage ? VIDEO_ASPECT_RATIO : image.aspect_ratio;
        const altText = productTitle + ' - ' + (idx + 1);

        return (
          <ImageContainer
            isSelected={idx === selectedImage}
            key={`carousel-image-${image.id || idx}`}
            onClick={() => setSelectedImage(idx)}
          >
            <Image
              src={imageSrc}
              width={IMAGE_WIDTH}
              aspectRatio={aspectRatio}
              animate={idx > visibleImageIdx}
              role={ROLES.BUTTON}
              aria-label="Select Image"
              data-at="pdp-product-image"
              alt={altText}
              placeholderImage={
                idx <= visibleImageIdx ? getOptimisedImageUrl(imageSrc, IMAGE_OPTION) : undefined
              }
              className={imgClassName}
            />
            {isVideoImage && <VideoPlayButton />}
          </ImageContainer>
        );
      })}
    </Carousel>
  );
}

export default memo(ImageSelector);
